import { differenceInMinutes, formatISO } from "date-fns";
import { parseDate } from "shared/utils";
import { useState, useMemo } from "react";
import { UpdateProfileFormValues } from "apps/directory/Operator/views";

export enum HoursOfOperationStatus {
  Open247 = "OPEN_247",
  Custom = "CUSTOM",
}

export const useHoursOfOperation = (
  hoursOfOperation: UpdateProfileFormValues["hoursOfOperation"],
) => {
  const isFullDay = (start: string | undefined, end: string | undefined) => {
    if (!start || !end) return null;
    // 0 minutes is the difference between hours 00:00:00 and 00:00:00
    // In the database, if a day is a full day, start === end
    return differenceInMinutes(parseDate(end), parseDate(start)) === 0;
  };

  const isEmptyDay = (start: string | undefined, end: string | undefined) => {
    return start === "" && end === "";
  };

  const getHoursOfOperationStatus = () => {
    if (
      !hoursOfOperation ||
      (isEmptyDay(hoursOfOperation?.sun?.start, hoursOfOperation?.sun?.end) &&
        isEmptyDay(hoursOfOperation?.mon?.start, hoursOfOperation?.mon?.end) &&
        isEmptyDay(hoursOfOperation?.tue?.start, hoursOfOperation?.tue?.end) &&
        isEmptyDay(hoursOfOperation?.wed?.start, hoursOfOperation?.wed?.end) &&
        isEmptyDay(hoursOfOperation?.thu?.start, hoursOfOperation?.thu?.end) &&
        isEmptyDay(hoursOfOperation?.fri?.start, hoursOfOperation?.fri?.end) &&
        isEmptyDay(hoursOfOperation?.sat?.start, hoursOfOperation?.sat?.end))
    ) {
      return null;
    } else if (
      isFullDay(hoursOfOperation?.sun?.start, hoursOfOperation?.sun?.end) &&
      isFullDay(hoursOfOperation?.mon?.start, hoursOfOperation?.mon?.end) &&
      isFullDay(hoursOfOperation?.tue?.start, hoursOfOperation?.tue?.end) &&
      isFullDay(hoursOfOperation?.wed?.start, hoursOfOperation?.wed?.end) &&
      isFullDay(hoursOfOperation?.thu?.start, hoursOfOperation?.thu?.end) &&
      isFullDay(hoursOfOperation?.fri?.start, hoursOfOperation?.fri?.end) &&
      isFullDay(hoursOfOperation?.sat?.start, hoursOfOperation?.sat?.end)
    ) {
      return HoursOfOperationStatus.Open247;
    } else {
      return HoursOfOperationStatus.Custom;
    }
  };

  /**
   * This base date is the Unix Epoch Start date
   * This will be used as the timeOptions base date & therefore what is saved
   * in the DB
   */
  const baseDate = useMemo(() => new Date(Date.UTC(1970, 0, 1)), []);

  const get247Hours = () => {
    return {
      sun: {
        start: formatISO(baseDate.setUTCHours(0, 0, 0, 0)),
        end: formatISO(baseDate.setUTCHours(0, 0, 0, 0)),
      },
      mon: {
        start: formatISO(baseDate.setUTCHours(0, 0, 0, 0)),
        end: formatISO(baseDate.setUTCHours(0, 0, 0, 0)),
      },
      tue: {
        start: formatISO(baseDate.setUTCHours(0, 0, 0, 0)),
        end: formatISO(baseDate.setUTCHours(0, 0, 0, 0)),
      },
      wed: {
        start: formatISO(baseDate.setUTCHours(0, 0, 0, 0)),
        end: formatISO(baseDate.setUTCHours(0, 0, 0, 0)),
      },
      thu: {
        start: formatISO(baseDate.setUTCHours(0, 0, 0, 0)),
        end: formatISO(baseDate.setUTCHours(0, 0, 0, 0)),
      },
      fri: {
        start: formatISO(baseDate.setUTCHours(0, 0, 0, 0)),
        end: formatISO(baseDate.setUTCHours(0, 0, 0, 0)),
      },
      sat: {
        start: formatISO(baseDate.setUTCHours(0, 0, 0, 0)),
        end: formatISO(baseDate.setUTCHours(0, 0, 0, 0)),
      },
    };
  };

  const getDayPath = (day: string) => {
    return day.toLowerCase() as keyof typeof hoursOfOperation;
  };

  const getIsDayEmpty = (day: string) => {
    const dayPath = getDayPath(day);
    return (
      hoursOfOperation[dayPath].start === "" &&
      hoursOfOperation[dayPath].end === ""
    );
  };

  const timeOptions = useMemo(() => {
    const hoursOfDay: { label: string; value: string }[] = [];

    const convertMinutesToTime = (min: number) => {
      // Calculate hours and minutes
      const hours = Math.floor(min / 60) % 24;
      const remainingMinutes = min % 60;

      // Adjust hours for the 12-hour clock
      const adjustedHours = hours > 12 ? hours - 12 : hours;

      // Format adjusted hours and minutes
      const formattedHours = adjustedHours.toString().padStart(2, "0");
      const formattedMinutes = remainingMinutes.toString().padStart(2, "0");

      // Determine AM or PM
      const period = hours < 12 ? "am" : "pm";

      // Build the final text
      return {
        text: `${formattedHours}:${formattedMinutes} ${period}`,
        hour: hours,
        minute: remainingMinutes,
      };
    };

    // Loop through the entire day (24 hours) in 30-minute increments
    for (let minutes = 0; minutes < 60 * 24; minutes += 30) {
      // Convert the current minute count to a time (e.g., 90 -> 1:30 AM)
      const { text, hour, minute } = convertMinutesToTime(minutes);
      // Add an entry for this time slot to the hoursOfDay array
      hoursOfDay.push({
        label: text, // A human-readable label for the time (e.g., "1:30 AM")
        value: formatISO(baseDate.setUTCHours(hour, minute, 0, 0)),
      });
    }

    return hoursOfDay;
  }, [baseDate]);

  const [hoursOfOperationStatus, setHoursOfOperationStatus] =
    useState<HoursOfOperationStatus | null>(getHoursOfOperationStatus());

  return {
    hoursOfOperationStatus,
    timeOptions,
    getHoursOfOperationStatus,
    setHoursOfOperationStatus,
    getDayPath,
    get247Hours,
    getIsDayEmpty,
  };
};
